import React from 'react';
import { useNavigate } from 'react-router-dom';

import { exchangeGmailOAuthCode } from '../services/gmailService';

function getQueryParam(name: string) {
  return new URLSearchParams(window.location.search).get(name);
}

export default function GmailCallback() {
  const navigate = useNavigate();
  const [status, setStatus] = React.useState<'working' | 'success' | 'error'>('working');
  const [message, setMessage] = React.useState('Connecting Gmail…');

  React.useEffect(() => {
    const run = async () => {
      try {
        const code = getQueryParam('code');
        const state = getQueryParam('state');
        const error = getQueryParam('error');

        if (error) {
          setStatus('error');
          setMessage(`Google OAuth error: ${error}`);
          return;
        }

        if (!code || !state) {
          setStatus('error');
          setMessage('Missing `code` or `state` in callback URL');
          return;
        }

        // In dev, React.StrictMode can mount/unmount/remount components which may cause this effect
        // to run twice. Exchanging the same OAuth code twice typically yields Google's `invalid_grant`.
        const lockKey = `gmail_oauth_exchange:${code}`;
        const existing = sessionStorage.getItem(lockKey);
        if (existing === 'done') {
          setStatus('success');
          setMessage('Gmail already connected. Redirecting…');
          setTimeout(() => navigate('/settings', { replace: true }), 300);
          return;
        }
        if (existing === 'in_progress') {
          // Another mount is already exchanging this code.
          setStatus('working');
          setMessage('Finishing Gmail connection…');
          return;
        }
        sessionStorage.setItem(lockKey, 'in_progress');

        const redirectUri = `${window.location.origin}/gmail/callback`;
        await exchangeGmailOAuthCode({ code, state, redirectUri });

        sessionStorage.setItem(lockKey, 'done');

        setStatus('success');
        setMessage('Gmail connected successfully. Redirecting…');
        setTimeout(() => navigate('/settings', { replace: true }), 800);
      } catch (e: any) {
        try {
          const code = getQueryParam('code');
          if (code) sessionStorage.removeItem(`gmail_oauth_exchange:${code}`);
        } catch {
          // ignore
        }
        const msg = e?.response?.data?.message || e?.message || 'Failed to exchange OAuth code';
        setStatus('error');
        setMessage(msg);
      }
    };

    run();
  }, [navigate]);

  return (
    <div className="p-6">
      <h2 className="text-xl font-semibold text-gray-900">Gmail Connection</h2>
      <p className="mt-2 text-gray-700">{message}</p>
      {status === 'error' && (
        <p className="mt-4 text-sm text-gray-600">
          Try again from Settings → Gmail. If it keeps failing, verify the redirect URI matches Google Cloud
          Console and the backend env vars are set.
        </p>
      )}
    </div>
  );
}
